// Licenses list — filter by customer/status + paginated table + issue / revoke /
// extend actions. Detail view is inline (expand row) to keep navigation flat.

import { useState } from 'react';
import {
  Button,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  type TablePaginationConfig,
} from 'antd';
import {
  CloseCircleOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useCustomers } from '../features/customers/useCustomers';
import {
  useExtendLicense,
  useLicenses,
  useRevokeLicense,
} from '../features/licenses/useLicenses';
import type { License, LicenseStatus } from '../features/licenses/licenses.api';
import { LicenseStatusBadge } from '../features/licenses/LicenseStatusBadge';
import { IssueLicenseModal } from '../features/licenses/IssueLicenseModal';
import { EditLicenseModal } from '../features/licenses/EditLicenseModal';

const { Title } = Typography;

const STATUS_OPTIONS: { value: LicenseStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
];

export function LicensesListPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<LicenseStatus | undefined>(undefined);
  const [issueOpen, setIssueOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState<License | null>(null);
  const [extendDays, setExtendDays] = useState(365);
  const [editTarget, setEditTarget] = useState<License | null>(null);

  const customersQuery = useCustomers({ page: 1, limit: 100, sort: 'name' });
  const { data, isLoading, isFetching } = useLicenses({
    page,
    limit,
    customerId,
    status,
    sort: '-createdAt',
  });
  const revoke = useRevokeLicense();
  const extend = useExtendLicense();

  async function handleRevoke(id: string) {
    const reason = window.prompt('Lý do thu hồi:') ?? '';
    if (!reason.trim()) return;
    try {
      await revoke.mutateAsync({ id, reason: reason.trim() });
      message.success('Đã thu hồi license');
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(apiMessage ?? 'Không thu hồi được');
    }
  }

  async function handleExtendOk() {
    if (!extendTarget) return;
    try {
      await extend.mutateAsync({ id: extendTarget.id, addDays: extendDays });
      message.success(`Đã gia hạn +${extendDays} ngày`);
      setExtendTarget(null);
    } catch (err) {
      const apiMessage = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(apiMessage ?? 'Không gia hạn được');
    }
  }

  const columns: ColumnsType<License> = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (v: string) => <Typography.Text code copyable>{v}</Typography.Text>,
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, row) => row.customer?.name ?? '—',
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: LicenseStatus) => <LicenseStatusBadge status={v} />,
    },
    {
      title: 'Instances',
      dataIndex: 'maxInstances',
      key: 'maxInstances',
      width: 90,
    },
    {
      title: 'Hết hạn',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('vi-VN'),
    },
    {
      title: 'Payment',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 110,
      render: (v: string) => {
        const color = v === 'paid' ? 'green' : v === 'refunded' ? 'volcano' : 'orange';
        const label = v === 'paid' ? 'Đã thanh toán' : v === 'refunded' ? 'Hoàn tiền' : 'Chưa thanh toán';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 220,
      render: (_, row) => (
        <Space size={4}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setEditTarget(row)}
            title="Sửa metadata (payment, plan, features, notes...)"
          />
          <Button
            type="text"
            icon={<ReloadOutlined />}
            disabled={row.status === 'revoked'}
            onClick={() => {
              setExtendTarget(row);
              setExtendDays(365);
            }}
            title="Gia hạn"
          />
          <Popconfirm
            title="Thu hồi license này?"
            okText="Thu hồi"
            cancelText="Huỷ"
            onConfirm={() => void handleRevoke(row.id)}
            disabled={row.status === 'revoked'}
          >
            <Button
              type="text"
              danger
              icon={<CloseCircleOutlined />}
              disabled={row.status === 'revoked'}
              title="Thu hồi"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize: limit,
    total: data?.meta.total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
  };

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Licenses</Title>
        <Space>
          <Select
            placeholder="Customer"
            allowClear
            style={{ width: 220 }}
            loading={customersQuery.isLoading}
            options={(customersQuery.data?.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={customerId}
            onChange={(v) => {
              setCustomerId(v ?? undefined);
              setPage(1);
            }}
            showSearch
            optionFilterProp="label"
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 140 }}
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v ?? undefined);
              setPage(1);
            }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIssueOpen(true)}>
            Issue license
          </Button>
        </Space>
      </Space>

      <Table<License>
        rowKey="id"
        columns={columns}
        dataSource={data?.data ?? []}
        loading={isLoading || isFetching}
        pagination={pagination}
        onChange={(pag) => {
          if (pag.current && pag.current !== page) setPage(pag.current);
          if (pag.pageSize && pag.pageSize !== limit) setLimit(pag.pageSize);
        }}
        scroll={{ x: 1100 }}
        expandable={{
          expandedRowRender: (row) => (
            <Space direction="vertical" size={4}>
              {row.domain && <span>Domain lock: <Typography.Text code>{row.domain}</Typography.Text></span>}
              {row.features.length > 0 && (
                <span>
                  Features:{' '}
                  {row.features.map((f) => (
                    <Tag color="blue" key={f}>{f}</Tag>
                  ))}
                </span>
              )}
              {row.paymentReference && <span>Hợp đồng: <Typography.Text code>{row.paymentReference}</Typography.Text></span>}
              {row.notes && <span>Ghi chú: {row.notes}</span>}
              {row.revokedAt && (
                <span>
                  Thu hồi lúc {new Date(row.revokedAt).toLocaleString('vi-VN')}: {row.revokedReason ?? ''}
                </span>
              )}
            </Space>
          ),
        }}
      />

      <IssueLicenseModal open={issueOpen} onClose={() => setIssueOpen(false)} />

      <EditLicenseModal
        open={!!editTarget}
        license={editTarget}
        onClose={() => setEditTarget(null)}
      />

      <Modal
        open={!!extendTarget}
        title={`Gia hạn license ${extendTarget?.key ?? ''}`}
        onCancel={() => setExtendTarget(null)}
        onOk={() => void handleExtendOk()}
        confirmLoading={extend.isPending}
        okText="Gia hạn"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <span>Số ngày gia hạn (cộng vào expiresAt hoặc từ now nếu đã hết hạn):</span>
          <InputNumber
            value={extendDays}
            onChange={(v) => setExtendDays(Number(v ?? 1))}
            min={1}
            max={3650}
            style={{ width: 200 }}
          />
        </Space>
      </Modal>
    </>
  );
}
